import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useFilters from '../useFilters';
import AnalyticsApi from 'libs/api/analytics';
import _ from 'lodash';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import { Table } from 'antd';
import { fileNameEdit } from 'helpers/FileUtils';
import Text from 'antd/es/typography/Text';
import { usePdf } from '../../../../hooks/usePdf';

const api = new AnalyticsApi('charts/footfalls/');

const format = (v) => `${v}%`;

const defaultState = {
  tableData: [],
  lineChartData: [],
  barChart: [],
  barChangeLine: [],
  total: {},
};

const useFootfall = (isPrint, allReportsData, isAllReports) => {
  console.log('allReportsData=', allReportsData);
  const { t } = useTranslation();
  const { handleDownloadPdf, loadingPdf, createCustomEvent } = usePdf();
  const [errors, setErrors] = useState([]);
  const { filters, title, searchParams } = useFilters(
    false,
    true,
    isAllReports,
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultState);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const [columns, setColumns] = useState([
    {
      title: t('Periodo'),
      dataIndex: 'Periodo',
      fixed: true,
    },
    {
      title: t('Footfall vs LY (+/-%)'),
      dataIndex: 'Footfall vs LY (+/-%)',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Incidenza % Footfall YTD'),
      dataIndex: 'Incidenza % Footfall YTD',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Incidenza % FootfallLY'),
      dataIndex: 'Incidenza % Footfall LY',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
  ]);

  const summary = useCallback(() => {
    const { total = {} } = data;

    delete total.Periodo;

    const tableKeys = Object.keys(total);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row fixed>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (!params.by_period || !params.by_shopping_center) return;
        let data;
        let total;
        if (!isAllReports) {
          const {
            data: { data: dataBack, total: totalBack },
          } = await api.getFootfalls({ params });
          data = dataBack;
          total = totalBack;
        } else {
          const { data: dataBack, total: totalBack } = allReportsData;
          data = dataBack;
          total = totalBack;
        }

        if (!data) return;

        const tableKeys = Object.keys(total);
        const filteredColumns = columns.filter((column) =>
          tableKeys.includes(column.dataIndex),
        );
        const lineChartData1 = {
          id: 'Footfall LY',
          // color: 'blue',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Footfall LY'],
          })),
        };
        const lineChartData2 = {
          id: 'Footfall YTD',
          // color: 'blue',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Footfall YTD'],
          })),
        };

        const maxValueFroLy = _.maxBy(data, 'Incidenza % Footfall LY')[
          'Incidenza % Footfall LY'
        ];
        const maxValueFroYtd = _.maxBy(data, 'Incidenza % Footfall YTD')[
          'Incidenza % Footfall YTD'
        ];

        const maxValue =
          maxValueFroLy > maxValueFroYtd ? maxValueFroLy : maxValueFroYtd;

        const barChart = data.map((item) => ({
          date: item['Periodo'],
          'Footfall LY': item['Incidenza % Footfall LY'],
          'Footfall LYColor': '#61cdbb',
          'Footfall YTD': item['Incidenza % Footfall YTD'],
          'Footfall YTDColor': '#f47560',
        }));

        const barChangeLine = {
          id: 'Footfall vs LY',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Footfall vs LY (+/-%)'],
            tooltipKeys: [
              {
                key: 'Footfall LY',
                value: Helpers.changeDotToComa(
                  format(item['Incidenza % Footfall LY']),
                ),
                color: '#61cdbb',
                percent: '',
              },
              {
                key: 'Footfall YTD',
                value: Helpers.changeDotToComa(
                  format(item['Incidenza % Footfall YTD']),
                ),
                color: '#f47560',
                percent: '',
              },
              {
                key: 'Footfall vs LY',
                value: Helpers.changeDotToComa(
                  format(item['Footfall vs LY (+/-%)']),
                ),
                color: '#1f77b4',
                percent: '',
              },
            ],
          })),
        };

        setData({
          tableData: data,
          lineChartData: [lineChartData1, lineChartData2],
          barChart,
          maxValue,
          barChangeLine: [barChangeLine],
          total,
        });
        setColumns(filteredColumns);
        setErrors([]);
      } catch (e) {
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setErrors(UIHelper.showAnalyticsError(e));
        console.log(e);
      } finally {
        setLoading(false);
        if (isPrint) {
          createCustomEvent();
        }
      }
    })();
  }, [searchParams, isPrint, isAllReports, allReportsData]);

  return {
    columns,
    data,
    filters,
    title,
    searchParams,
    format,
    loading,
    handleDownloadExcel,
    summary,
    handleDownloadPdf,
    loadingPdf,
    errors,
  };
};

export default useFootfall;
